import * as React from 'react';
import { useFormikContext } from 'formik';
import { DateTime } from 'luxon';
import Litepicker from 'litepicker';
import 'litepicker/dist/plugins/keyboardnav';
import 'litepicker/dist/plugins/mobilefriendly';

function discardKeyChanges(event) {
  event.preventDefault();
  event.stopPropagation();
}

export const useLitePicker = (inputName, config = {}) => {
  const { postChange, ...litePickerConfig } = config;
  const litepicker = React.useRef(null);
  const input = React.useRef(null);
  const lastSelection = React.useRef(null);
  const { setFieldValue } = useFormikContext();

  const register = () => {
    input.current = document.querySelector(`input[name=${inputName}]`);

    if (!input.current) {
      return;
    }

    input.current.addEventListener('keydown', discardKeyChanges);

    const getPosition = () => {
      const availableSpace = document.documentElement.clientHeight - input.current.getBoundingClientRect().y;
      return availableSpace > 320 ? 'bottom auto' : 'top auto';
    };

    litepicker.current = new Litepicker({
      element: input.current,
      format: 'MM/DD/YYYY',
      singleMode: false,
      numberOfColumns: 2,
      numberOfMonths: 2,
      resetButton: true,
      position: getPosition(),
      plugins: ['keyboardnav', 'mobilefriendly'],
      ...litePickerConfig,
    });

    litepicker.current.on('selected', () => {
      setTimeout(() => {
        const { value } = input.current;
        lastSelection.current = value;
        setFieldValue(inputName, value);

        if (typeof postChange === 'function') {
          postChange(DateTime.cast(value, 'MM/dd/yyyy')?.toISODate());
        }
      });
    });

    litepicker.current.on('before:show', () => {
      litepicker.current.options.position = getPosition();
    });
  };

  const unregister = () => {
    if (!litepicker.current) {
      return;
    }

    input.current.removeEventListener('keydown', discardKeyChanges);
    litepicker.current.destroy();
    litepicker.current = null;
  };

  React.useEffect(() => {
    // Input value has been cleared manually or through Formik
    if (input.current && !input.current.value && lastSelection.current) {
      litepicker.current.clearSelection();
      lastSelection.current = '';
    }
  }, [input.current?.value]);

  // To be used with React.useEffect
  return () => {
    register();
    return unregister;
  };
};
